import React, { useEffect, useState } from 'react';
import { SendIcon, PlusIcon, SearchIcon, TrashIcon, PrinterIcon, BanIcon, CheckCircleIcon, ClockIcon, TruckIcon, FileTextIcon, UserIcon, MapPinIcon, CalendarIcon, AlertCircleIcon, EyeIcon, EditIcon, PackageIcon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { StatsCard } from '../components/ui/StatsCard';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { Textarea } from '../components/ui/Textarea';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
import { useSortableData } from '../hooks/useSortableData';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { supabaseAdmin } from '../lib/supabase';
import { Conduce, ConduceLine, Invoice, Product } from '../types';
const ITEMS_PER_PAGE = 10;
const emptyForm = {
  tipoDocumento: 'factura' as 'factura' | 'prefactura',
  fecha: new Date().toISOString().split('T')[0],
  clienteId: '',
  clienteNombre: '',
  clienteCodigo: '',
  facturaNumero: '',
  documento: '',
  localidad: 'ALMACEN PRINCIPAL',
  despachoDesde: '',
  tipoDespacho: '',
  fechaEntrega: '',
  comentarios: '',
  transportistaCompania: '',
  transportistaDireccion: '',
  conductorNombre: '',
  conductorCedula: '',
  vehiculoMatricula: '',
  conductorTelefono: '',
  estado: 'pendiente' as Conduce['estado']
};
export function ConducesPage() {
  const {
    user,
    addActivityLog
  } = useAuth();
  const {
    clients,
    users
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  // List State
  const [conduces, setConduces] = useState<Conduce[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view'>('create');
  const [selectedConduce, setSelectedConduce] = useState<Conduce | null>(null);
  const [activeTab, setActiveTab] = useState<'generales' | 'cliente' | 'transportistas'>('generales');
  const [isSaving, setIsSaving] = useState(false);
  // Form State
  const [formData, setFormData] = useState({
    ...emptyForm
  });
  const [formLines, setFormLines] = useState<Omit<ConduceLine, 'id' | 'conduceId'>[]>([]);
  // Auxiliary Data
  const [clientInvoices, setClientInvoices] = useState<Invoice[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [invoiceItems, setInvoiceItems] = useState<any[]>([]); // Mocked for "Facturados o Prefacturados"
  // Article Entry State
  const [selectedProduct, setSelectedProduct] = useState('');
  const [articleQty, setArticleQty] = useState('1');
  const [articleUnit, setArticleUnit] = useState('UND');
  useEffect(() => {
    fetchConduces();
    fetchProducts();
  }, []);
  const fetchConduces = async () => {
    setIsLoading(true);
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('conduces').select('*').order('created_at', {
        ascending: false
      });
      if (error) throw error;
      if (data) {
        const mapped: Conduce[] = data.map((row: any) => ({
          id: row.id,
          numeroCond: row.numero_conduce,
          tipoDocumento: row.tipo_documento,
          fecha: new Date(row.fecha),
          clienteId: row.cliente_id,
          clienteNombre: row.cliente_nombre,
          clienteCodigo: row.cliente_codigo,
          facturaNumero: row.factura_numero,
          documento: row.documento,
          localidad: row.localidad,
          despachoDesde: row.despacho_desde,
          tipoDespacho: row.tipo_despacho,
          fechaEntrega: row.fecha_entrega ? new Date(row.fecha_entrega) : null,
          comentarios: row.comentarios,
          vendedor: row.vendedor,
          vendedorId: row.vendedor_id,
          conductorNombre: row.conductor_nombre,
          conductorCedula: row.conductor_cedula,
          vehiculoMatricula: row.vehiculo_matricula,
          conductorTelefono: row.conductor_telefono,
          estado: row.estado,
          lineas: [],
          createdAt: new Date(row.created_at)
        }));
        setConduces(mapped);
      }
    } catch (err) {
      console.error('Error fetching conduces:', err);
    } finally {
      setIsLoading(false);
    }
  };
  const fetchProducts = async () => {
    try {
      const {
        data
      } = await supabaseAdmin.from('products').select('id, nombre, sku, precio').order('nombre');
      if (data) setProducts(data);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };
  const fetchClientInvoices = async (clienteNombre: string) => {
    try {
      const {
        data
      } = await supabaseAdmin.from('invoices').select('*').eq('farmacia', clienteNombre).neq('estado', 'pagada').order('fecha_emision', {
        ascending: false
      });
      if (data) {
        setClientInvoices(data.map((row: any) => ({
          id: row.id,
          numeroFactura: row.numero_factura,
          farmacia: row.farmacia,
          vendedor: row.vendedor,
          fechaEmision: new Date(row.fecha_emision),
          fechaVencimiento: new Date(row.fecha_vencimiento),
          subTotal: row.sub_total,
          itbis: row.itbis,
          monto: row.monto,
          saldoPendiente: row.saldo_pendiente,
          estado: row.estado
        })));
      } else {
        setClientInvoices([]);
      }
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setClientInvoices([]);
    }
  };
  const fetchConduceLines = async (conduceId: string) => {
    try {
      const {
        data
      } = await supabaseAdmin.from('conduce_lines').select('*').eq('conduce_id', conduceId);
      if (data) {
        setFormLines(data.map((row: any) => ({
          productoCodigo: row.producto_codigo,
          productoNombre: row.producto_nombre,
          cantidad: row.cantidad,
          unidad: row.unidad
        })));
      }
    } catch (err) {
      console.error('Error fetching conduce lines:', err);
    }
  };
  // Handle Client Selection — clear old invoice + items when client changes
  useEffect(() => {
    if (formData.clienteId) {
      const client = clients.find(c => c.id === formData.clienteId);
      if (client) {
        setFormData(prev => ({
          ...prev,
          clienteNombre: client.nombre,
          clienteCodigo: client.codigo || '',
          facturaNumero: '' // Reset factura when client changes
        }));
        setInvoiceItems([]); // Clear old invoice items
        fetchClientInvoices(client.nombre);
      }
    } else {
      setClientInvoices([]);
      setInvoiceItems([]);
      setFormData(prev => ({
        ...prev,
        facturaNumero: '',
        clienteNombre: '',
        clienteCodigo: ''
      }));
    }
  }, [formData.clienteId]);
  // Fetch real order_lines when an invoice is selected
  useEffect(() => {
    if (!formData.facturaNumero || !formData.clienteNombre) {
      setInvoiceItems([]);
      return;
    }
    const fetchInvoiceItems = async () => {
      try {
        // Find orders for this farmacia to get their order_lines
        const {
          data: orders
        } = await supabaseAdmin.from('orders').select('id').eq('farmacia', formData.clienteNombre).limit(50);
        if (!orders || orders.length === 0) {
          setInvoiceItems([]);
          return;
        }
        const orderIds = orders.map((o: any) => o.id);
        const {
          data: lines
        } = await supabaseAdmin.from('order_lines').select('sku, nombre, cantidad, precio').in('order_id', orderIds);
        if (lines && lines.length > 0) {
          // Agrupar por SKU para evitar duplicados y sumar cantidades
          const grouped: Record<string, {
            codigo: string;
            descripcion: string;
            cantidad: number;
            precio: number;
          }> = {};
          lines.forEach((l: any) => {
            const key = l.sku || l.nombre;
            if (grouped[key]) {
              grouped[key].cantidad += l.cantidad;
            } else {
              grouped[key] = {
                codigo: l.sku || '',
                descripcion: l.nombre,
                cantidad: l.cantidad,
                precio: l.precio || 0
              };
            }
          });
          setInvoiceItems(Object.values(grouped));
        } else {
          setInvoiceItems([]);
        }
      } catch (err) {
        console.error('Error fetching invoice items:', err);
        setInvoiceItems([]);
      }
    };
    fetchInvoiceItems();
  }, [formData.facturaNumero, formData.clienteNombre]);
  const handleOpenModal = (mode: 'create' | 'edit' | 'view', conduce?: Conduce) => {
    setModalMode(mode);
    setSelectedConduce(conduce || null);
    setActiveTab('generales');
    if (mode === 'create') {
      setFormData({
        ...emptyForm
      });
      setFormLines([]);
    } else if (conduce) {
      setFormData({
        tipoDocumento: conduce.tipoDocumento,
        fecha: conduce.fecha.toISOString().split('T')[0],
        clienteId: conduce.clienteId,
        clienteNombre: conduce.clienteNombre,
        clienteCodigo: conduce.clienteCodigo,
        facturaNumero: conduce.facturaNumero,
        documento: conduce.documento,
        localidad: conduce.localidad,
        despachoDesde: conduce.despachoDesde,
        tipoDespacho: conduce.tipoDespacho,
        fechaEntrega: conduce.fechaEntrega ? conduce.fechaEntrega.toISOString().split('T')[0] : '',
        comentarios: conduce.comentarios,
        conductorNombre: conduce.conductorNombre,
        conductorCedula: conduce.conductorCedula,
        vehiculoMatricula: conduce.vehiculoMatricula,
        conductorTelefono: conduce.conductorTelefono,
        estado: conduce.estado
      });
      fetchConduceLines(conduce.id);
      fetchClientInvoices(conduce.clienteNombre);
    }
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedConduce(null);
    setFormData({
      ...emptyForm
    });
    setFormLines([]);
  };
  const handleAddArticle = () => {
    if (!selectedProduct || !articleQty) return;
    const product = products.find(p => p.id === selectedProduct);
    if (!product) return;
    setFormLines(prev => [...prev, {
      productoCodigo: product.sku || product.id.substring(0, 8),
      productoNombre: product.nombre,
      cantidad: parseInt(articleQty, 10),
      unidad: articleUnit
    }]);
    setSelectedProduct('');
    setArticleQty('1');
  };
  const handleRemoveArticle = (index: number) => {
    setFormLines(prev => prev.filter((_, i) => i !== index));
  };
  const handleAddAllInvoiceItems = () => {
    const newLines = invoiceItems.map(item => ({
      productoCodigo: item.codigo,
      productoNombre: item.descripcion,
      cantidad: item.cantidad,
      unidad: 'UND'
    }));
    setFormLines(prev => [...prev, ...newLines]);
  };
  const handleSave = async (printAfter: boolean = false) => {
    if (!formData.clienteId) {
      alert('Debe seleccionar un cliente');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        tipo_documento: formData.tipoDocumento,
        fecha: new Date(formData.fecha).toISOString(),
        cliente_id: formData.clienteId,
        cliente_nombre: formData.clienteNombre,
        cliente_codigo: formData.clienteCodigo,
        factura_numero: formData.facturaNumero,
        documento: formData.documento,
        localidad: formData.localidad,
        despacho_desde: formData.despachoDesde,
        tipo_despacho: formData.tipoDespacho,
        fecha_entrega: formData.fechaEntrega ? new Date(formData.fechaEntrega).toISOString() : null,
        comentarios: formData.comentarios,
        vendedor: user?.nombre || '',
        vendedor_id: user?.id || null,
        conductor_nombre: formData.conductorNombre,
        conductor_cedula: formData.conductorCedula,
        vehiculo_matricula: formData.vehiculoMatricula,
        conductor_telefono: formData.conductorTelefono,
        estado: formData.estado
      };
      let conduceId = '';
      if (modalMode === 'create') {
        const {
          data,
          error
        } = await supabaseAdmin.from('conduces').insert(payload).select().single();
        if (error) throw error;
        conduceId = data.id;
        addActivityLog('Crear Conduce', `Conduce creado para ${formData.clienteNombre}`);
      } else if (selectedConduce) {
        const {
          error
        } = await supabaseAdmin.from('conduces').update(payload).eq('id', selectedConduce.id);
        if (error) throw error;
        conduceId = selectedConduce.id;
        // Delete old lines
        await supabaseAdmin.from('conduce_lines').delete().eq('conduce_id', conduceId);
        addActivityLog('Editar Conduce', `Conduce ${selectedConduce.numeroCond} editado`);
      }
      // Insert new lines
      if (formLines.length > 0 && conduceId) {
        const linesPayload = formLines.map(line => ({
          conduce_id: conduceId,
          producto_codigo: line.productoCodigo,
          producto_nombre: line.productoNombre,
          cantidad: line.cantidad,
          unidad: line.unidad
        }));
        const {
          error: linesError
        } = await supabaseAdmin.from('conduce_lines').insert(linesPayload);
        if (linesError) throw linesError;
      }
      await fetchConduces();
      if (printAfter) {
        window.print(); // Placeholder for actual print logic
      }
      handleCloseModal();
    } catch (err) {
      console.error('Error saving conduce:', err);
      alert('Error al guardar el conduce. Por favor intente de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };
  const handleAnular = async () => {
    if (!selectedConduce) return;
    if (!window.confirm('¿Está seguro que desea anular este conduce?')) return;
    setIsSaving(true);
    try {
      const {
        error
      } = await supabaseAdmin.from('conduces').update({
        estado: 'anulado'
      }).eq('id', selectedConduce.id);
      if (error) throw error;
      addActivityLog('Anular Conduce', `Conduce ${selectedConduce.numeroCond} anulado`);
      await fetchConduces();
      handleCloseModal();
    } catch (err) {
      console.error('Error anular conduce:', err);
      alert('Error al anular el conduce.');
    } finally {
      setIsSaving(false);
    }
  };
  // Filtering
  const filteredConduces = conduces.filter(c => {
    if (!isAdmin && c.vendedorId !== user?.id) return false;
    if (dateFrom && new Date(c.fecha) < new Date(dateFrom)) return false;
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59);
      if (new Date(c.fecha) > to) return false;
    }
    if (statusFilter && c.estado !== statusFilter) return false;
    if (vendorFilter && c.vendedorId !== vendorFilter) return false;
    if (clientSearch && !c.clienteNombre.toLowerCase().includes(clientSearch.toLowerCase())) return false;
    return true;
  });
  const {
    items: sortedConduces,
    requestSort,
    sortConfig
  } = useSortableData(filteredConduces, {
    numero: c => c.numeroCond,
    fecha: c => new Date(c.fecha).getTime(),
    cliente: c => c.clienteNombre,
    estado: c => c.estado
  });
  const finalConduces = sortConfig ? sortedConduces : [...sortedConduces].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  const totalPages = Math.ceil(finalConduces.length / ITEMS_PER_PAGE);
  const paginatedConduces = finalConduces.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  // Stats
  const stats = {
    total: filteredConduces.length,
    pendientes: filteredConduces.filter(c => c.estado === 'pendiente').length,
    despachados: filteredConduces.filter(c => c.estado === 'despachado').length,
    entregados: filteredConduces.filter(c => c.estado === 'entregado').length
  };
  const getStatusBadge = (status: string) => {
    const config: Record<string, {
      bg: string;
      text: string;
      icon: React.ReactNode;
    }> = {
      pendiente: {
        bg: 'bg-amber-100',
        text: 'text-amber-700',
        icon: <ClockIcon className="w-3.5 h-3.5" data-id="element-1021" />
      },
      despachado: {
        bg: 'bg-blue-100',
        text: 'text-blue-700',
        icon: <SendIcon className="w-3.5 h-3.5" data-id="element-1022" />
      },
      en_transito: {
        bg: 'bg-indigo-100',
        text: 'text-indigo-700',
        icon: <TruckIcon className="w-3.5 h-3.5" data-id="element-1023" />
      },
      entregado: {
        bg: 'bg-emerald-100',
        text: 'text-emerald-700',
        icon: <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-1024" />
      },
      anulado: {
        bg: 'bg-slate-100',
        text: 'text-slate-600',
        icon: <BanIcon className="w-3.5 h-3.5" data-id="element-1025" />
      }
    };
    const style = config[status] || config.pendiente;
    return <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold ${style.bg} ${style.text}`} data-id="element-1026">
        {style.icon}
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>;
  };
  const formatDate = (date: Date) => new Date(date).toLocaleDateString('es-DO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const clientOptions = clients.map(c => ({
    value: c.id,
    label: c.nombre
  }));
  const invoiceOptions = clientInvoices.map(i => ({
    value: i.numeroFactura,
    label: `${i.numeroFactura} - ${new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP'
    }).format(i.monto)}`
  }));
  const productOptions = products.map(p => ({
    value: p.id,
    label: `${p.sku || p.id.substring(0, 8)} - ${p.nombre}`
  }));
  const vendorOptions = [{
    value: '',
    label: 'Todos los vendedores'
  }, ...users.filter(u => u.rolNombre !== 'Administrador').map(u => ({
    value: u.id,
    label: u.nombre
  }))];
  const selectedClientData = clients.find(c => c.id === formData.clienteId);
  return <div className="space-y-6" data-id="element-1027">
      <PageHeader title="Conduces" subtitle="Gestión de notas de entrega y despacho" icon={<SendIcon className="w-6 h-6" data-id="element-1029" />} data-id="element-1028">
        <Button variant="primary" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1031" />} onClick={() => handleOpenModal('create')} data-id="element-1030">
          Nuevo Conduce
        </Button>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-stagger-1" data-id="element-1032">
        <StatsCard title="Total Conduces" value={stats.total} icon={<FileTextIcon className="w-5 h-5" data-id="element-1034" />} variant="primary" data-id="element-1033" />
        <StatsCard title="Pendientes" value={stats.pendientes} icon={<ClockIcon className="w-5 h-5" data-id="element-1036" />} variant="warning" data-id="element-1035" />
        <StatsCard title="Despachados" value={stats.despachados} icon={<SendIcon className="w-5 h-5" data-id="element-1038" />} variant="default" data-id="element-1037" />
        <StatsCard title="Entregados" value={stats.entregados} icon={<CheckCircleIcon className="w-5 h-5" data-id="element-1040" />} variant="success" data-id="element-1039" />
      </div>

      {/* Filters */}
      <Card className="animate-stagger-2" data-id="element-1041">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3" data-id="element-1042">
          <div className="col-span-2 lg:col-span-2" data-id="element-1043">
            <Input placeholder="Buscar por cliente..." value={clientSearch} onChange={e => {
            setClientSearch(e.target.value);
            setCurrentPage(1);
          }} leftIcon={<SearchIcon className="w-4 h-4" data-id="element-1045" />} data-id="element-1044" />
          </div>
          <div className="col-span-1" data-id="element-1046">
            <Input type="date" value={dateFrom} onChange={e => {
            setDateFrom(e.target.value);
            setCurrentPage(1);
          }} data-id="element-1047" />
          </div>
          <div className="col-span-1" data-id="element-1048">
            <Input type="date" value={dateTo} onChange={e => {
            setDateTo(e.target.value);
            setCurrentPage(1);
          }} data-id="element-1049" />
          </div>
          <div className="col-span-1" data-id="element-1050">
            <Select options={[{
            value: '',
            label: 'Todos los estados'
          }, {
            value: 'pendiente',
            label: 'Pendiente'
          }, {
            value: 'despachado',
            label: 'Despachado'
          }, {
            value: 'en_transito',
            label: 'En Tránsito'
          }, {
            value: 'entregado',
            label: 'Entregado'
          }, {
            value: 'anulado',
            label: 'Anulado'
          }]} value={statusFilter} onChange={e => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }} data-id="element-1051" />
          </div>
          {isAdmin && <div className="col-span-1" data-id="element-1052">
              <Select options={vendorOptions} value={vendorFilter} onChange={e => {
            setVendorFilter(e.target.value);
            setCurrentPage(1);
          }} data-id="element-1053" />
            </div>}
        </div>
      </Card>

      {/* Mobile List */}
      <div className="md:hidden space-y-3 animate-stagger-3" data-id="element-1054">
        {paginatedConduces.map(conduce => <Card key={conduce.id} className="p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleOpenModal('view', conduce)} data-id="element-1055">
            <div className="flex justify-between items-start mb-2" data-id="element-1056">
              <div data-id="element-1057">
                <p className="font-bold text-slate-900" data-id="element-1058">{conduce.numeroCond}</p>
                <p className="text-sm font-medium text-slate-700" data-id="element-1059">
                  {conduce.clienteNombre}
                </p>
              </div>
              {getStatusBadge(conduce.estado)}
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-500 mt-3" data-id="element-1060">
              <span className="flex items-center gap-1" data-id="element-1061">
                <CalendarIcon className="w-3.5 h-3.5" data-id="element-1062" />{' '}
                {formatDate(conduce.fecha)}
              </span>
              {conduce.facturaNumero && <span className="flex items-center gap-1" data-id="element-1063">
                  <FileTextIcon className="w-3.5 h-3.5" data-id="element-1064" />{' '}
                  {conduce.facturaNumero}
                </span>}
            </div>
          </Card>)}
      </div>

      {/* Desktop Table */}
      <Card padding="none" className="hidden md:block animate-stagger-3" data-id="element-1065">
        <div className="overflow-x-auto" data-id="element-1066">
          <table className="w-full" data-id="element-1067">
            <thead data-id="element-1068">
              <tr className="bg-slate-50 border-b border-slate-100" data-id="element-1069">
                <SortableTableHeader label="No. Conduce" sortKey="numero" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1070" />
                <SortableTableHeader label="Fecha" sortKey="fecha" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1071" />
                <SortableTableHeader label="Cliente" sortKey="cliente" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1072" />
                <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1073">
                  Factura
                </th>
                <th className="px-5 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1074">
                  Localidad
                </th>
                <SortableTableHeader label="Estado" sortKey="estado" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1075" />
                <th className="px-5 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-1076">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1077">
              {paginatedConduces.map(conduce => <tr key={conduce.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-1078">
                  <td className="px-5 py-4 font-medium text-slate-900" data-id="element-1079">
                    {conduce.numeroCond}
                  </td>
                  <td className="px-5 py-4 text-sm text-slate-600" data-id="element-1080">
                    {formatDate(conduce.fecha)}
                  </td>
                  <td className="px-5 py-4" data-id="element-1081">
                    <p className="font-medium text-slate-900 text-sm" data-id="element-1082">
                      {conduce.clienteNombre}
                    </p>
                    {isAdmin && <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5" data-id="element-1083">
                        <UserIcon className="w-3 h-3" data-id="element-1084" /> {conduce.vendedor}
                      </p>}
                  </td>
                  <td className="px-5 py-4 text-sm text-slate-600" data-id="element-1085">
                    {conduce.facturaNumero || '—'}
                  </td>
                  <td className="px-5 py-4 text-sm text-slate-600" data-id="element-1086">
                    {conduce.localidad || '—'}
                  </td>
                  <td className="px-5 py-4" data-id="element-1087">
                    {getStatusBadge(conduce.estado)}
                  </td>
                  <td className="px-5 py-4 text-right space-x-2" data-id="element-1088">
                    <Button variant="ghost" size="sm" onClick={() => handleOpenModal('view', conduce)} data-id="element-1089">
                      <EyeIcon className="w-4 h-4 text-slate-400" data-id="element-1090" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleOpenModal('edit', conduce)} data-id="element-1091">
                      <EditIcon className="w-4 h-4 text-slate-400" data-id="element-1092" />
                    </Button>
                  </td>
                </tr>)}
              {paginatedConduces.length === 0 && <tr data-id="element-1093">
                  <td colSpan={7} className="px-5 py-8 text-center text-slate-500" data-id="element-1094">
                    No se encontraron conduces.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredConduces.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-1095" />}

      {/* Create/Edit/View Modal */}
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={modalMode === 'create' ? 'Nuevo Conduce' : modalMode === 'edit' ? `Editar Conduce ${selectedConduce?.numeroCond}` : `Detalle Conduce ${selectedConduce?.numeroCond}`} size="lg" data-id="element-1096">
        <ModalBody data-id="element-1097">
          {/* Tabs */}
          <div className="flex border-b border-slate-200 mb-6" data-id="element-1098">
            {[{
            id: 'generales',
            label: 'Datos Generales'
          }, {
            id: 'transportistas',
            label: 'Transportistas'
          }].map(tab => <button key={tab.id} className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === tab.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`} onClick={() => setActiveTab(tab.id as any)} data-id="element-1099">
                {tab.label}
              </button>)}
          </div>

          {/* Tab: Datos Generales */}
          {activeTab === 'generales' && <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4" data-id="element-1100">
                {/* Left Column */}
                <div className="space-y-4" data-id="element-1101">
                  <Input label="Conduce No." value={modalMode === 'create' ? 'Auto-generado' : selectedConduce?.numeroCond || ''} disabled data-id="element-1102" />
                  <Input label="Fecha" type="date" value={formData.fecha} onChange={e => setFormData({
                ...formData,
                fecha: e.target.value
              })} disabled={modalMode === 'view'} data-id="element-1103" />
                  <SearchableSelect label="Cliente" options={clientOptions} value={formData.clienteId} onChange={val => setFormData({
                ...formData,
                clienteId: val
              })} placeholder="Buscar cliente..." disabled={modalMode === 'view'} data-id="element-1104" />
                  <SearchableSelect label="Factura" options={invoiceOptions} value={formData.facturaNumero} onChange={val => setFormData({
                ...formData,
                facturaNumero: val
              })} placeholder="Seleccionar factura..." disabled={modalMode === 'view' || !formData.clienteId} data-id="element-1105" />
                  <Input label="Documento" value={formData.documento} onChange={e => setFormData({
                ...formData,
                documento: e.target.value
              })} placeholder="Ej: 1 BULTO" disabled={modalMode === 'view'} data-id="element-1106" />
                </div>

                {/* Right Column */}
                <div className="space-y-4" data-id="element-1107">
                  <Input label="Localidad" value={formData.localidad} onChange={e => setFormData({
                ...formData,
                localidad: e.target.value
              })} disabled={modalMode === 'view'} data-id="element-1108" />
                  <Input label="Despacho desde" value={formData.despachoDesde} onChange={e => setFormData({
                ...formData,
                despachoDesde: e.target.value
              })} disabled={modalMode === 'view'} data-id="element-1109" />
                  <Textarea label="Comentarios" value={formData.comentarios} onChange={e => setFormData({
                ...formData,
                comentarios: e.target.value
              })} disabled={modalMode === 'view'} rows={2} placeholder="Comentarios..." data-id="element-1110" />
                  <Input label="Fecha Entrega" type="date" value={formData.fechaEntrega} onChange={e => setFormData({
                ...formData,
                fechaEntrega: e.target.value
              })} disabled={modalMode === 'view'} data-id="element-1111" />
                  <Input label="Tipo Despacho" value={formData.tipoDespacho} onChange={e => setFormData({
                ...formData,
                tipoDespacho: e.target.value
              })} disabled={modalMode === 'view'} data-id="element-1112" />
                </div>
              </div>

              {/* Estado (solo en edición) */}
              {modalMode === 'edit' && <div className="mt-4 max-w-xs" data-id="element-1113">
                  <Select label="Estado" options={[{
              value: 'pendiente',
              label: 'Pendiente'
            }, {
              value: 'despachado',
              label: 'Despachado'
            }, {
              value: 'en_transito',
              label: 'En Tránsito'
            }, {
              value: 'entregado',
              label: 'Entregado'
            }, {
              value: 'anulado',
              label: 'Anulado'
            }]} value={formData.estado} onChange={e => setFormData({
              ...formData,
              estado: e.target.value as any
            })} data-id="element-1114" />
                </div>}
            </>}

          {/* Tab: Transportistas */}
          {activeTab === 'transportistas' && <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4" data-id="element-1115">
              {/* Left Column */}
              <div className="space-y-4" data-id="element-1116">
                <Input label="Compañía" value={formData.transportistaCompania} onChange={e => setFormData({
              ...formData,
              transportistaCompania: e.target.value
            })} placeholder="Nombre de la compañía..." disabled={modalMode === 'view'} data-id="element-1117" />
                <Input label="Dirección" value={formData.transportistaDireccion} onChange={e => setFormData({
              ...formData,
              transportistaDireccion: e.target.value
            })} placeholder="Dirección del transportista..." disabled={modalMode === 'view'} data-id="element-1118" />
              </div>

              {/* Right Column */}
              <div className="space-y-4" data-id="element-1119">
                <Input label="Teléfono" value={formData.conductorTelefono} onChange={e => setFormData({
              ...formData,
              conductorTelefono: e.target.value
            })} placeholder="000-000-0000" disabled={modalMode === 'view'} data-id="element-1120" />
                <Input label="Conductor" value={formData.conductorNombre} onChange={e => setFormData({
              ...formData,
              conductorNombre: e.target.value
            })} disabled={modalMode === 'view'} data-id="element-1121" />
                <Input label="Cédula" value={formData.conductorCedula} onChange={e => setFormData({
              ...formData,
              conductorCedula: e.target.value
            })} placeholder="000-0000000-0" disabled={modalMode === 'view'} data-id="element-1122" />
                <Input label="Matrícula" value={formData.vehiculoMatricula} onChange={e => setFormData({
              ...formData,
              vehiculoMatricula: e.target.value
            })} disabled={modalMode === 'view'} data-id="element-1123" />
              </div>
            </div>}
        </ModalBody>
        <ModalFooter data-id="element-1124">
          <div className="flex flex-wrap items-center justify-between w-full gap-2" data-id="element-1125">
            <div className="flex gap-2" data-id="element-1126">
              {modalMode === 'edit' && selectedConduce?.estado !== 'anulado' && <Button variant="danger" onClick={handleAnular} isLoading={isSaving} data-id="element-1127">
                    Anular
                  </Button>}
              {modalMode === 'view' && <Button variant="secondary" leftIcon={<PrinterIcon className="w-4 h-4" data-id="element-1129" />} onClick={() => window.print()} data-id="element-1128">
                  Imprimir
                </Button>}
            </div>
            <div className="flex gap-2" data-id="element-1130">
              <Button variant="ghost" onClick={handleCloseModal} disabled={isSaving} data-id="element-1131">
                {modalMode === 'view' ? 'Cerrar' : 'Cancelar'}
              </Button>
              {modalMode !== 'view' && <>
                  <Button variant="secondary" onClick={() => handleSave(true)} isLoading={isSaving} leftIcon={<PrinterIcon className="w-4 h-4" data-id="element-1133" />} data-id="element-1132">
                    Guardar e Imprimir
                  </Button>
                  <Button variant="primary" onClick={() => handleSave(false)} isLoading={isSaving} data-id="element-1134">
                    Guardar
                  </Button>
                </>}
            </div>
          </div>
        </ModalFooter>
      </Modal>
    </div>;
}